// src/pages/ccp/BakingCcpVerifyPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import bakingCcpService from '../../services/bakingCcpService';
import ingredientService from '../../services/ingredientService';
import ingredientInventoryService from '../../services/ingredientInventoryService';
import productService from '../../services/productService';
import sqfService from '../../services/sqfService';
import { usePermissions } from '../../context/PermissionContext';
import { useLanguage, t } from '../../context/LanguageContext';
import { getCcpFlowLabels } from '../../config/ccpFlowLabels';
import VisualInspectionsList from '../../components/VisualInspectionsList';

export default function BakingCcpVerifyPage() {
  const { lang } = useLanguage();
  const { runId } = useParams();
  const navigate = useNavigate();
  const { companySettings } = usePermissions();
  const deviationThreshold = companySettings?.ingredientDeviationThreshold ?? 5;

  const [loading, setLoading] = useState(true);
  const [run, setRun] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [visualInspections, setVisualInspections] = useState([]);
  const errorRef = useRef(null);

  // Any action on this page (ingredients, lot codes, verify, etc.) can set `error` from far down
  // the page — scroll it into view so a failure doesn't silently sit off-screen above the fold.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  // Editable QA fields
  const [productTotals, setProductTotals] = useState([]);

  // Recipe cache for expected quantity calculations
  const recipeCacheRef = useRef({});
  const [deviation, setDeviation] = useState(false);
  const [deviationNotes, setDeviationNotes] = useState('');
  const [correctiveNotes, setCorrectiveNotes] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Ingredient lot codes
  const [ingredients, setIngredients] = useState([]);
  const [loadedBatchId, setLoadedBatchId] = useState(null); // authoritative batchId for ingredient ops
  const [loadingIngredients, setLoadingIngredients] = useState(false);
  const [savingIngredients, setSavingIngredients] = useState(false);
  const [ingredientsSaved, setIngredientsSaved] = useState(false);
  const [newRow, setNewRow] = useState({ ingredientId: '', ingredientLotCode: '', quantityInput: '', uomInput: 'kg' });
  const [allIngredients, setAllIngredients] = useState([]);
  // { [ingredientId]: [{ lotCode, remainingKg, lastDeliveryDate }] }
  const [availableLotCodes, setAvailableLotCodes] = useState({});

  // Editable lot codes
  const [editLotCode,    setEditLotCode]    = useState('');
  const [editFgLotCode,  setEditFgLotCode]  = useState('');
  const [savingLotCodes, setSavingLotCodes] = useState(false);
  const [lotCodesSaved,  setLotCodesSaved]  = useState(false);

  // Confirm modal
  const [showConfirm, setShowConfirm] = useState(false);

  const fetchRun = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await bakingCcpService.getRun(runId);
      const r = res?.run || null;
      setRun(r);
      if (r) {
        seedEditableFields(r);
        if (r.batchId) fetchIngredients(r.batchId);
        if (r.batchId) {
          // No productId filter — a run can schedule several products, so this shows every
          // Visual Inspection recorded against the batch regardless of which product it was for.
          sqfService.getChecklistLogsForBatch(r.batchId).then(setVisualInspections).catch(() => setVisualInspections([]));
        }
      }
    } catch (e) {
      console.error(e);
      setError(t('Failed to load run.', 'Error al cargar la corrida.', lang));
    } finally {
      setLoading(false);
    }
  };

  const fetchIngredients = async (batchId) => {
    setLoadingIngredients(true);
    try {
      const res = await bakingCcpService.getBatchIngredients(batchId);
      const rows = Array.isArray(res) ? res : [];
      setIngredients(rows);
      // Fetch available lot codes for each unique ingredient
      const uniqueIds = [...new Set(rows.map(r => r.ingredientId).filter(Boolean))];
      if (uniqueIds.length > 0) {
        const entries = await Promise.all(
          uniqueIds.map(id =>
            ingredientInventoryService.getLotCodesAvailable(id)
              .then(lcs => [id, lcs])
              .catch(() => [id, []])
          )
        );
        setAvailableLotCodes(Object.fromEntries(entries));
      }
    } catch (e) {
      console.error('Failed to load ingredients', e);
      setIngredients([]);
    } finally {
      setLoadedBatchId(batchId);
      setLoadingIngredients(false);
    }
  };

  const seedEditableFields = (r) => {
    // Product totals: prefer productTotalsJson, fall back to computing from carts
    if (Array.isArray(r.productTotalsJson) && r.productTotalsJson.length > 0) {
      setProductTotals(r.productTotalsJson.map(x => ({ ...x })));
    } else {
      // Compute from carts that have been blasted out
      const totalsMap = new Map();
      (r.Carts || []).forEach(c => {
        if (!c.blastOutAt) return;
        const pid = Number(c.productId);
        const name = c.Product?.name || `Product #${pid}`;
        const units = Number(c.unitsInCart) || 0;
        if (totalsMap.has(pid)) {
          totalsMap.get(pid).units += units;
        } else {
          totalsMap.set(pid, { productId: pid, name, units });
        }
      });
      setProductTotals(Array.from(totalsMap.values()));
    }

    setDeviation(!!r.deviation);
    setDeviationNotes(r.deviationNotes || '');
    setCorrectiveNotes(r.correctiveActionNotes || '');
    setNotes(r.notes || r.packagingNotes || '');
    setEditLotCode(r.Batch?.lotCode || '');
    setEditFgLotCode(r.finishedGoodsLotCode || '');
  };

  useEffect(() => {
    fetchRun();
    ingredientService.getIngredients()
      .then(data => setAllIngredients(Array.isArray(data) ? data : []))
      .catch(() => setAllIngredients([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Receiving document map: { [ingredientId:lotCode]: receivingLog }
  const [receivingDocsMap, setReceivingDocsMap] = useState({});

  // Fetch recipes for all products in productTotals and compute expected kg per ingredient
  const [expectedKgMap, setExpectedKgMap] = useState({});
  useEffect(() => {
    if (!productTotals.length) { setExpectedKgMap({}); return; }
    let cancelled = false;
    (async () => {
      const map = {};
      for (const pt of productTotals) {
        const units = Number(pt.units) || 0;
        if (!units) continue;
        let recipe = recipeCacheRef.current[pt.productId];
        if (!recipe) {
          try {
            const raw = await productService.getProductRecipe(pt.productId);
            recipe = raw.allIngredients || (Array.isArray(raw) ? raw : (raw.ingredients || []));
            recipeCacheRef.current[pt.productId] = recipe;
          } catch (e) {
            console.error('Failed to fetch recipe for product', pt.productId, e);
            continue;
          }
        }
        for (const item of recipe) {
          if (item.expectedQuantityKg != null) {
            map[item.ingredientId] = (map[item.ingredientId] || 0) + Number(item.expectedQuantityKg) * units;
          }
        }
      }
      if (!cancelled) setExpectedKgMap(map);
    })();
    return () => { cancelled = true; };
  }, [productTotals]);

  const editedTotal = useMemo(() => {
    return productTotals.reduce((sum, r) => {
      const n = Number(r.units);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [productTotals]);

  const isLocked = run?.isLocked || run?.status === 'VERIFIED';
  const L = getCcpFlowLabels(run?.flowType);

  // Fetch receiving docs for all ingredients in this batch
  useEffect(() => {
    if (!run?.batchId) return;
    let cancelled = false;
    ingredientInventoryService.getByBatch(run.batchId)
      .then(data => {
        if (cancelled) return;
        const map = {};
        (Array.isArray(data) ? data : []).forEach(({ ingredientId, ingredientLotCode, receivingLog }) => {
          if (receivingLog) map[`${ingredientId}:${ingredientLotCode}`] = receivingLog;
        });
        setReceivingDocsMap(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [run?.batchId]);

  const toKg = (qty, uom) => {
    const n = Number(qty);
    if (!Number.isFinite(n) || n < 0) return null;
    return uom === 'kg' ? n : n * 0.45359237;
  };

  const saveIngredients = async () => {
    setSavingIngredients(true);
    setError('');
    try {
      // Sum all lot-code rows per ingredient before computing deviation
      const totalKgByIngredient = {};
      for (const ing of ingredients) {
        const uom = ing.uomInput || 'kg';
        const qInput = ing.quantityInput != null ? Number(ing.quantityInput) : null;
        const qKg = (qInput != null && Number.isFinite(qInput) && qInput > 0) ? toKg(qInput, uom) : null;
        if (qKg != null) {
          totalKgByIngredient[ing.ingredientId] = (totalKgByIngredient[ing.ingredientId] || 0) + qKg;
        }
      }

      const payload = ingredients.map(ing => {
        const uom = ing.uomInput || 'kg';
        const qInput = ing.quantityInput != null ? Number(ing.quantityInput) : null;
        const qKg = (qInput != null && Number.isFinite(qInput) && qInput > 0) ? toKg(qInput, uom) : null;
        const lot = (ing.ingredientLotCode || '').trim();
        let status = 'MISSING';
        if (lot) status = (qKg != null && qKg > 0) ? 'ENTERED' : 'EXCLUDED';
        const expectedKg = expectedKgMap[ing.ingredientId] ?? null;
        const totalKg = totalKgByIngredient[ing.ingredientId] ?? null;
        let deviationPct = null;
        if (totalKg != null && expectedKg != null && expectedKg > 0) {
          deviationPct = ((totalKg - expectedKg) / expectedKg) * 100;
        }
        return {
          id: ing.id,
          ingredientLotCode: lot || null,
          quantityInput: qInput,
          uomInput: uom,
          quantityKg: qKg,
          status,
          deviationPct,
          deviationJustification: ing.deviationJustification || null,
        };
      });
      const targetBatchId = loadedBatchId ?? run?.batchId;
      if (!targetBatchId) throw new Error('Batch ID not available. Please reload the page.');
      await bakingCcpService.updateBatchIngredients(targetBatchId, payload);
      await fetchIngredients(targetBatchId);
      setIngredientsSaved(true);
      setTimeout(() => setIngredientsSaved(false), 2500);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || t('Failed to save ingredients.', 'Error al guardar los ingredientes.', lang));
    } finally {
      setSavingIngredients(false);
    }
  };

  const addIngredientRow = async () => {
    if (!newRow.ingredientId) return;
    const targetBatchId = loadedBatchId ?? run?.batchId;
    if (!targetBatchId) return;

    const uom = newRow.uomInput || 'kg';
    const qInput = newRow.quantityInput !== '' ? Number(newRow.quantityInput) : null;
    const qKg = (qInput != null && Number.isFinite(qInput) && qInput > 0) ? toKg(qInput, uom) : null;
    const lot = (newRow.ingredientLotCode || '').trim();
    const status = lot ? (qKg != null ? 'ENTERED' : 'EXCLUDED') : 'MISSING';

    try {
      setSavingIngredients(true);
      await bakingCcpService.addBatchIngredient(targetBatchId, {
        ingredientId: Number(newRow.ingredientId),
        ingredientLotCode: lot || null,
        quantityInput: qInput,
        uomInput: uom,
        quantityKg: qKg,
        status,
      });
      // Refresh from server
      await fetchIngredients(targetBatchId);
      setNewRow({ ingredientId: '', ingredientLotCode: '', quantityInput: '', uomInput: 'kg' });
      setIngredientsSaved(false);
    } catch (e) {
      setError(e?.response?.data?.message || t('Failed to add ingredient.', 'Error al agregar el ingrediente.', lang));
    } finally {
      setSavingIngredients(false);
    }
  };

  const removeIngredientRow = async (ing) => {
    const targetBatchId = loadedBatchId ?? run?.batchId;
    if (!targetBatchId) return;
    if (!window.confirm(`${t('Remove', 'Eliminar', lang)} ${ing.Ingredient?.name || t('this ingredient', 'este ingrediente', lang)}${ing.ingredientLotCode ? ` (${ing.ingredientLotCode})` : ''} ${t('from this batch?', 'de este lote?', lang)}`)) return;
    try {
      setSavingIngredients(true);
      await bakingCcpService.deleteBatchIngredient(targetBatchId, ing.id);
      await fetchIngredients(targetBatchId);
    } catch (e) {
      setError(e?.response?.data?.message || t('Failed to remove ingredient.', 'Error al eliminar el ingrediente.', lang));
    } finally {
      setSavingIngredients(false);
    }
  };

  const saveLotCodes = async () => {
    const lot = editLotCode.trim();
    if (!lot) { setError(t('Lot code cannot be empty.', 'El código de lote no puede estar vacío.', lang)); return; }
    setSavingLotCodes(true);
    setError('');
    try {
      await bakingCcpService.updateLotCodes(runId, {
        lotCode: lot,
        finishedGoodsLotCode: editFgLotCode.trim() || null,
      });
      setLotCodesSaved(true);
      setTimeout(() => setLotCodesSaved(false), 2500);
      await fetchRun();
    } catch (e) {
      setError(e?.response?.data?.message || t('Failed to save lot codes.', 'Error al guardar los códigos de lote.', lang));
    } finally {
      setSavingLotCodes(false);
    }
  };

  const doVerify = async () => {
    setShowConfirm(false);
    setError('');
    setSubmitting(true);
    try {
      await bakingCcpService.verifyAndLog(runId, {
        productTotals,
        notes,
        deviation,
        deviationNotes,
        correctiveActionNotes: correctiveNotes,
      });
      setSuccessMsg(t('Run verified and production log created successfully.', 'Corrida verificada y registro de producción creado exitosamente.', lang));
      // Refresh navbar QA badge count
      window.dispatchEvent(new Event('productionStatusChanged'));
      setTimeout(() => navigate('/ccp/baking/queue'), 1800);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e?.message || t('Failed to verify run.', 'Error al verificar la corrida.', lang));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatDate = (d) => {
    if (!d) return '—';
    // Plain YYYY-MM-DD strings are parsed as UTC midnight by new Date(), which
    // rolls back one day in negative-offset timezones. Appending T00:00:00
    // forces the date to be interpreted in local time instead.
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d;
    return new Date(normalized).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const carts = useMemo(() => {
    return [...(run?.Carts || [])].sort((a, b) => (a.cartNumber || 0) - (b.cartNumber || 0));
  }, [run]);

  const tempReadings = useMemo(() => {
    return [...(run?.TempReadings || [])].sort((a, b) => new Date(a.readingAt) - new Date(b.readingAt));
  }, [run]);

  const packagingAnswers = useMemo(() => {
    const q = run?.packagingAnswersJson;
    return Array.isArray(q) ? q : [];
  }, [run]);

  if (loading) return (
    <div className="container mt-4 text-center text-muted py-5">
      <div className="spinner-border spinner-border-sm me-2" /> {t('Loading run…', 'Cargando corrida…', lang)}
    </div>
  );

  if (!run && !loading) return (
    <div className="container mt-4">
      <div className="alert alert-danger">{t('Run not found.', 'Corrida no encontrada.', lang)}</div>
      <Link className="btn btn-outline-secondary" to="/ccp/baking/queue">← {t('Back to Queue', 'Volver a la Cola', lang)}</Link>
    </div>
  );

  return (
    <>
    <style>{`
      @media print {
        /* Hide everything by default */
        body * { visibility: hidden; }

        /* Show only the report content */
        .sqf-report, .sqf-report * { visibility: visible; }
        .sqf-report { position: absolute; top: 0; left: 0; width: 100%; }

        /* Hide interactive / nav elements even inside the report */
        .no-print { display: none !important; }

        /* Typography & layout */
        .sqf-report { font-size: 11pt; font-family: Arial, sans-serif; }
        .sqf-report h3 { font-size: 15pt; margin-bottom: 2pt; }
        .sqf-report h6 { font-size: 10pt; text-transform: uppercase;
                         letter-spacing: 0.05em; color: #555; margin-bottom: 6pt; }
        .sqf-report .card { border: 1px solid #ccc !important;
                            page-break-inside: avoid; margin-bottom: 10pt; }
        .sqf-report .card-body { padding: 8pt 10pt; }
        .sqf-report table { width: 100%; border-collapse: collapse; font-size: 10pt; }
        .sqf-report th, .sqf-report td { border: 1px solid #ddd;
                                          padding: 3pt 5pt; text-align: left; }
        .sqf-report th { background: #f5f5f5; font-weight: bold; }
        .sqf-report .badge { border: 1px solid #999; padding: 1pt 4pt;
                              font-size: 9pt; background: transparent !important;
                              color: #000 !important; }

        /* Print header with company + report title */
        .print-header { display: block !important; }

        /* Page breaks */
        .page-break-before { page-break-before: always; }

        /* Two-column layout collapses to single column */
        .row { display: block !important; }
        .col-12, .col-lg-6 { width: 100% !important; max-width: 100% !important;
                              display: block !important; }

        /* Ensure status badge colours render in print */
        .bg-success { background-color: #d4edda !important; }
        .bg-danger  { background-color: #f8d7da !important; }
        .bg-warning { background-color: #fff3cd !important; }
        .bg-secondary { background-color: #e2e3e5 !important; }
      }

      /* Force full paper width — override Bootstrap container */
      .sqf-report.container,
      .sqf-report.container-sm,
      .sqf-report.container-md,
      .sqf-report.container-lg,
      .sqf-report.container-xl,
      .sqf-report.container-xxl {
        max-width: 100% !important;
        width: 100% !important;
        padding-left: 12pt !important;
        padding-right: 12pt !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
      }

      /* Print header hidden on screen */
      .print-header { display: none; }
    `}</style>
    <div className="container mt-4 pb-5 sqf-report">

      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-4">
        <div>
          <Link className="text-muted no-print" style={{ fontSize: 13 }} to="/ccp/baking/queue">
            ← {t('Back to Queue', 'Volver a la Cola', lang)}
          </Link>
          <h3 className="mb-0 mt-1">
            {t('QA Review', 'Revisión QA', lang)} — {run.Batch?.lotCode || `Batch #${run.batchId}`}
          </h3>
          <div className="text-muted" style={{ fontSize: 13 }}>
            {run.Product?.name} · {t('Production date:', 'Fecha de producción:', lang)} {formatDate(run.Batch?.production_date)}
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {isLocked && (
            <>
              <span className="badge bg-success" style={{ fontSize: 13, padding: '8px 12px' }}>
                ✓ {t('VERIFIED', 'VERIFICADO', lang)}
              </span>
              <button
                className="btn btn-outline-secondary btn-sm no-print"
                onClick={() => window.print()}
                title={t('Print or save as PDF', 'Imprimir o guardar como PDF', lang)}
              >
                🖨 {t('Print / Export PDF', 'Imprimir / Exportar PDF', lang)}
              </button>
            </>
          )}
          {!isLocked && (
            <span className="badge bg-warning text-dark" style={{ fontSize: 13, padding: '8px 12px' }}>
              {t('PENDING VERIFICATION', 'VERIFICACIÓN PENDIENTE', lang)}
            </span>
          )}
        </div>
      </div>

      <div className="no-print">
        <VisualInspectionsList inspections={visualInspections} />
      </div>

      {/* Print-only header */}
      <div className="print-header mb-3 pb-2" style={{ borderBottom: '2px solid #1b2638' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, color: '#1b2638' }}>PIZZACINI — SQF {L.stage1Action.en} CCP Record</div>
            <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>
              Lot: {run?.Batch?.lotCode || '—'} &nbsp;|&nbsp;
              FG Lot: {run?.finishedGoodsLotCode || '—'} &nbsp;|&nbsp;
              Prod. Date: {formatDate(run?.Batch?.production_date)}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 11, color: '#555' }}>
            <div>Verified: {formatDateTime(run?.verifiedAt)}</div>
            <div>By: {run?.VerifiedBy?.username || '—'}</div>
            <div style={{ marginTop: 4, fontWeight: 700, color: '#155724' }}>✓ VERIFIED &amp; LOCKED</div>
          </div>
        </div>
      </div>

      {error && <div ref={errorRef} className="alert alert-danger no-print">{error}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      <div className="row g-3">

        {/* ── Run summary card ── */}
        <div className="col-12 col-lg-6">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">{t('Run Summary', 'Resumen de Corrida', lang)}</h6>
              <table className="table table-sm table-borderless mb-0">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: 160 }}>{t('Status', 'Estado', lang)}</td>
                    <td><strong>{run.status}</strong></td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t('Lot Code', 'Código de Lote', lang)}</td>
                    <td>
                      {isLocked ? (
                        <strong>{run.Batch?.lotCode || '—'}</strong>
                      ) : (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editLotCode}
                          onChange={e => { setEditLotCode(e.target.value); setLotCodesSaved(false); }}
                          placeholder={t('Lot code', 'Código de lote', lang)}
                        />
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t('FG Lot Code', 'Código de Lote PT', lang)}</td>
                    <td>
                      {isLocked ? (
                        run.finishedGoodsLotCode || '—'
                      ) : (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editFgLotCode}
                          onChange={e => { setEditFgLotCode(e.target.value); setLotCodesSaved(false); }}
                          placeholder={t('FG lot code (optional)', 'Código de lote PT (opcional)', lang)}
                        />
                      )}
                    </td>
                  </tr>
                  {!isLocked && (
                    <tr>
                      <td colSpan={2} className="pt-2">
                        <button
                          className={`btn btn-sm ${lotCodesSaved ? 'btn-success' : 'btn-outline-primary'}`}
                          onClick={saveLotCodes}
                          disabled={savingLotCodes}
                        >
                          {savingLotCodes ? t('Saving…', 'Guardando…', lang) : lotCodesSaved ? `✓ ${t('Saved', 'Guardado', lang)}` : t('Save Lot Codes', 'Guardar Códigos de Lote', lang)}
                        </button>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="text-muted">{t('Prod. Date', 'Fecha Prod.', lang)}</td>
                    <td>{formatDate(run.Batch?.production_date)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t(`${L.stage1Action.en} Started`, `${L.stage1Action.es} Iniciado`, lang)}</td>
                    <td>{formatDateTime(run.bakingStartedAt)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t(`${L.stage1Action.en} Stopped`, `${L.stage1Action.es} Detenido`, lang)}</td>
                    <td>{formatDateTime(run.bakingStoppedAt)}</td>
                  </tr>
                  {!run.noBake && (
                    <tr>
                      <td className="text-muted">{t(`${L.vessel.en} Temp (start)`, `Temp. ${L.vessel.es} (inicio)`, lang)}</td>
                      <td><strong>{run.ovenTempStartF ? `${run.ovenTempStartF}°F` : '—'}</strong></td>
                    </tr>
                  )}
                  {run.flowType === 'pasteurization' && (
                    <tr>
                      <td className="text-muted">{t(`${L.vessel.en} Temp (stop)`, `Temp. ${L.vessel.es} (detención)`, lang)}</td>
                      <td><strong>{run.pasteurizationTempEndF != null ? `${run.pasteurizationTempEndF}°F` : '—'}</strong></td>
                    </tr>
                  )}
                  {run.flowType === 'pasteurization' && run.agingStartedAt && (
                    <tr>
                      <td className="text-muted">{t('Aging Started', 'Añejamiento Iniciado', lang)}</td>
                      <td>{formatDateTime(run.agingStartedAt)}</td>
                    </tr>
                  )}
                  {run.flowType === 'pasteurization' && run.agingEndedAt && (
                    <tr>
                      <td className="text-muted">{t('Aging Ended', 'Añejamiento Terminado', lang)}</td>
                      <td>{formatDateTime(run.agingEndedAt)}</td>
                    </tr>
                  )}
                  {run.noBake && (
                    <tr>
                      <td className="text-muted">{t('Production Type', 'Tipo de Producción', lang)}</td>
                      <td><span className="badge bg-info text-dark">🧊 {t('No-Bake', 'Sin Horneado', lang)}</span></td>
                    </tr>
                  )}
                  <tr>
                    <td className="text-muted">{t('Completed At', 'Completado el', lang)}</td>
                    <td>{formatDateTime(run.completedAt)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t('Completed By', 'Completado por', lang)}</td>
                    <td>{run.CompletedBy?.username || '—'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t('Record Owner', 'Responsable', lang)}</td>
                    <td>{run.RecordOwner?.username || '—'}</td>
                  </tr>
                  {isLocked && (
                    <>
                      <tr>
                        <td className="text-muted">{t('Verified At', 'Verificado el', lang)}</td>
                        <td>{formatDateTime(run.verifiedAt)}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">{t('Verified By', 'Verificado por', lang)}</td>
                        <td><strong className="text-success">{run.VerifiedBy?.username || '—'}</strong></td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Temperature readings card ── */}
        <div className="col-12 col-lg-6">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">
                {t('Temperature Readings', 'Lecturas de Temperatura', lang)} ({tempReadings.length})
              </h6>
              {run?.noBake ? (
                <div className="text-muted" style={{ fontSize: 13 }}>
                  🧊 {t('No-bake production — no temperature records.', 'Producción sin horneado — sin registros de temperatura.', lang)}
                </div>
              ) : tempReadings.length === 0 ? (
                <div className="text-muted" style={{ fontSize: 13 }}>{t('No readings recorded.', 'Sin lecturas registradas.', lang)}</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('Time', 'Hora', lang)}</th>
                        <th>{t('Temp (°F)', 'Temp. (°F)', lang)}</th>
                        <th>{t(L.unit.en, L.unit.es, lang)}</th>
                        <th>{t('Type', 'Tipo', lang)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tempReadings.map((reading, idx) => (
                        <tr key={reading.id}>
                          <td>{idx + 1}</td>
                          <td style={{ fontSize: 12 }}>
                            {new Date(reading.readingAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td><strong>{reading.tempF}°F</strong></td>
                          <td>{reading.cartId ? `#${reading.cartId}` : '—'}</td>
                          <td style={{ fontSize: 11 }} className="text-muted">{reading.readingType || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Carts card ── */}
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">
                {t(L.unitPlural.en, L.unitPlural.es, lang)} ({carts.length})
              </h6>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>{t(L.unit.en, L.unit.es, lang)}</th>
                      <th>{t('Product', 'Producto', lang)}</th>
                      <th>{t('Units', 'Unidades', lang)}</th>
                      <th>{t(L.vesselOutLabel.en, L.vesselOutLabel.es, lang)}</th>
                      <th>{t(L.blastInLabel.en, L.blastInLabel.es, lang)}</th>
                      <th>{t(L.blastOutLabel.en, L.blastOutLabel.es, lang)}</th>
                      <th>{t('Freezer Time', 'Tiempo Congelador', lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carts.length === 0 ? (
                      <tr><td colSpan={7} className="text-muted">{t('No records.', 'Sin registros.', lang)}</td></tr>
                    ) : carts.map(c => {
                      const blastIn = c.blastInAt ? new Date(c.blastInAt) : null;
                      const blastOut = c.blastOutAt ? new Date(c.blastOutAt) : null;
                      const freezerMins = blastIn && blastOut
                        ? Math.round((blastOut - blastIn) / 60000)
                        : null;
                      const prodName = c.Product?.name || `Product #${c.productId}`;
                      return (
                        <tr key={c.id}>
                          <td style={{ fontWeight: 900 }}>#{c.cartNumber}</td>
                          <td>{prodName}</td>
                          <td>{c.unitsInCart ?? '—'}</td>
                          <td style={{ fontSize: 12 }}>
                            {c.ovenOutAt ? new Date(c.ovenOutAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '—'}
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {blastIn ? blastIn.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : (
                              <span className="badge bg-danger">{t('Missing', 'Faltante', lang)}</span>
                            )}
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {blastOut ? blastOut.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : (
                              <span className="badge bg-danger">{t('Missing', 'Faltante', lang)}</span>
                            )}
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {freezerMins != null ? `${freezerMins} min` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ── Ingredient Lot Codes ── */}
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="card-title text-muted mb-0">{t('Ingredient Lot Codes', 'Códigos de Lote de Ingredientes', lang)}</h6>
                  {!isLocked && (
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      {t('Review and complete all ingredient lot codes and quantities before verifying.', 'Revisa y completa todos los códigos de lote y cantidades de ingredientes antes de verificar.', lang)}
                    </div>
                  )}
                </div>
                {!isLocked && (
                  <button
                    className={`btn btn-sm ${ingredientsSaved ? 'btn-success' : 'btn-outline-primary'}`}
                    onClick={saveIngredients}
                    disabled={savingIngredients || loadingIngredients}
                    style={{ minWidth: 120 }}
                  >
                    {savingIngredients ? t('Saving…', 'Guardando…', lang) : ingredientsSaved ? `✓ ${t('Saved', 'Guardado', lang)}` : t('Save Changes', 'Guardar Cambios', lang)}
                  </button>
                )}
              </div>

              {loadingIngredients ? (
                <div className="text-muted" style={{ fontSize: 13 }}>
                  <span className="spinner-border spinner-border-sm me-2" />{t('Loading ingredients…', 'Cargando ingredientes…', lang)}
                </div>
              ) : (
                <>
                  {/* Table — shown when there are rows, empty state otherwise */}
                  {ingredients.length === 0 ? (
                    <div className={`alert mb-3 ${isLocked ? 'alert-secondary' : 'alert-warning'}`} style={{ fontSize: 13 }}>
                      {isLocked
                        ? t('No ingredients were recorded for this batch.', 'No se registraron ingredientes para este lote.', lang)
                        : `⚠ ${t('No ingredients yet. Use the form below to add them.', 'Sin ingredientes aún. Usa el formulario abajo para agregarlos.', lang)}`}
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th style={{ minWidth: 160 }}>{t('Ingredient', 'Ingrediente', lang)}</th>
                              <th style={{ minWidth: 180 }}>{t('Lot Code', 'Código de Lote', lang)}</th>
                              <th style={{ minWidth: 110 }}>{t('Quantity', 'Cantidad', lang)}</th>
                              <th style={{ width: 80 }}>{t('Unit', 'Unidad', lang)}</th>
                              <th style={{ minWidth: 130 }}>{t('Expected (kg)', 'Esperado (kg)', lang)}</th>
                              <th style={{ width: 100 }}>{t('Status', 'Estado', lang)}</th>
                              {!isLocked && <th style={{ width: 50 }}></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {ingredients.map((ing, idx) => {
                              const statusColor = { ENTERED: 'success', MISSING: 'danger', EXCLUDED: 'secondary' };
                              const expectedKg = expectedKgMap[ing.ingredientId] ?? null;

                              // Live computed deviation uses the ingredient total across all lot-code rows
                              const liveTotalKg = (() => {
                                let total = 0;
                                for (const i of ingredients) {
                                  if (i.ingredientId !== ing.ingredientId) continue;
                                  const u = i.uomInput || 'kg';
                                  const k = (i.quantityInput != null && i.quantityInput !== '') ? toKg(Number(i.quantityInput), u) : null;
                                  if (k != null) total += k;
                                }
                                return total > 0 ? total : null;
                              })();

                              let deviationPct = null;
                              if (liveTotalKg != null && expectedKg != null && expectedKg > 0) {
                                deviationPct = ((liveTotalKg - expectedKg) / expectedKg) * 100;
                              } else if (isLocked && ing.deviationPct != null) {
                                deviationPct = Number(ing.deviationPct);
                              }

                              const hasDeviation = deviationPct !== null && Math.abs(deviationPct) > deviationThreshold;
                              const showJustificationRow = (!isLocked && hasDeviation) || (isLocked && !!ing.deviationJustification);
                              const colSpan = isLocked ? 6 : 7;

                              return (
                                <React.Fragment key={ing.id}>
                                <tr>
                                  <td style={{ fontWeight: 700 }}>
                                    {ing.Ingredient?.name || `Ingredient #${ing.ingredientId}`}
                                  </td>
                                  <td>
                                    {isLocked ? (
                                      <span className={!ing.ingredientLotCode ? 'text-danger' : ''}>
                                        {ing.ingredientLotCode || '—'}
                                      </span>
                                    ) : (() => {
                                      const datalistId = `lc-list-${ing.id}`;
                                      const available = availableLotCodes[ing.ingredientId] || [];
                                      const enteredLc = (ing.ingredientLotCode || '').trim().toUpperCase();
                                      const notInInventory = enteredLc && available.length > 0 &&
                                        !available.some(a => a.lotCode.toUpperCase() === enteredLc);
                                      return (
                                        <>
                                          <datalist id={datalistId}>
                                            {available.map(a => (
                                              <option key={a.lotCode} value={a.lotCode}>
                                                {a.lotCode} — {Number(a.remainingKg).toFixed(1)} kg remaining
                                              </option>
                                            ))}
                                          </datalist>
                                          <input
                                            type="text"
                                            list={datalistId}
                                            className={`form-control form-control-sm ${!ing.ingredientLotCode ? 'border-danger' : ''}`}
                                            value={ing.ingredientLotCode || ''}
                                            placeholder={t('Enter lot code', 'Ingresa código de lote', lang)}
                                            onChange={e => {
                                              const val = e.target.value;
                                              setIngredients(prev => prev.map((x, i) =>
                                                i === idx ? { ...x, ingredientLotCode: val } : x
                                              ));
                                              setIngredientsSaved(false);
                                            }}
                                          />
                                          {notInInventory && (
                                            <div className="text-warning mt-1" style={{ fontSize: 11 }}>
                                              ⚠ {t('Lot code not found in inventory', 'Código de lote no encontrado en inventario', lang)}
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </td>
                                  <td>
                                    {isLocked ? (
                                      <span>{ing.quantityInput != null ? ing.quantityInput : '—'} {ing.uomInput || ''}</span>
                                    ) : (
                                      <input
                                        type="number"
                                        className={`form-control form-control-sm ${hasDeviation ? 'border-warning' : ''}`}
                                        value={ing.quantityInput != null ? ing.quantityInput : ''}
                                        min={0}
                                        step="0.01"
                                        placeholder="0"
                                        onChange={e => {
                                          const val = e.target.value === '' ? null : e.target.value;
                                          setIngredients(prev => prev.map((x, i) =>
                                            i === idx ? { ...x, quantityInput: val } : x
                                          ));
                                          setIngredientsSaved(false);
                                        }}
                                      />
                                    )}
                                  </td>
                                  <td>
                                    {isLocked ? (
                                      <span>{ing.uomInput || '—'}</span>
                                    ) : (
                                      <select
                                        className="form-select form-select-sm"
                                        value={ing.uomInput || 'kg'}
                                        onChange={e => {
                                          const val = e.target.value;
                                          setIngredients(prev => prev.map((x, i) =>
                                            i === idx ? { ...x, uomInput: val } : x
                                          ));
                                          setIngredientsSaved(false);
                                        }}
                                      >
                                        <option value="lb">lb</option>
                                        <option value="kg">kg</option>
                                      </select>
                                    )}
                                  </td>
                                  <td style={{ fontSize: 13 }}>
                                    {expectedKg != null ? (
                                      <>
                                        <span>{Number(expectedKg).toFixed(3)} kg</span>
                                        {deviationPct !== null && (
                                          <span
                                            className={`ms-1 badge ${hasDeviation ? 'bg-warning text-dark' : 'bg-success'}`}
                                            style={{ fontSize: 10 }}
                                          >
                                            {deviationPct > 0 ? '+' : ''}{deviationPct.toFixed(1)}%
                                          </span>
                                        )}
                                      </>
                                    ) : deviationPct !== null ? (
                                      // No expected kg available (recipe deleted?) but stored deviation exists
                                      <span className={`badge ${hasDeviation ? 'bg-warning text-dark' : 'bg-success'}`} style={{ fontSize: 10 }}>
                                        {deviationPct > 0 ? '+' : ''}{deviationPct.toFixed(1)}%
                                      </span>
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                  <td>
                                    <span className={`badge bg-${statusColor[ing.status] || 'secondary'}`}>
                                      {ing.status}
                                    </span>
                                  </td>
                                  {!isLocked && (
                                    <td>
                                      <button
                                        className="btn btn-outline-danger btn-sm"
                                        style={{ padding: '1px 7px', fontSize: 16, lineHeight: 1 }}
                                        onClick={() => removeIngredientRow(ing)}
                                        disabled={savingIngredients}
                                        title={t('Remove this ingredient row', 'Eliminar esta fila de ingrediente', lang)}
                                      >
                                        ×
                                      </button>
                                    </td>
                                  )}
                                </tr>
                                {showJustificationRow && (
                                  <tr>
                                    <td colSpan={colSpan} className="p-0">
                                      {isLocked ? (
                                        <div className="px-3 py-2 bg-light border-top" style={{ fontSize: 12 }}>
                                          <span className="fw-semibold me-2">
                                            ⚠ Deviation{deviationPct !== null ? ` (${deviationPct > 0 ? '+' : ''}${deviationPct.toFixed(1)}%)` : ''}:
                                          </span>
                                          {ing.deviationJustification}
                                        </div>
                                      ) : (
                                        <div className="alert alert-warning py-2 px-3 mb-0 rounded-0" style={{ fontSize: 13 }}>
                                          <strong>⚠ {t('Quantity deviation of', 'Desviación de cantidad del', lang)} {deviationPct.toFixed(1)}% {t('detected', 'detectada', lang)}</strong> ({t('threshold:', 'umbral:', lang)} {deviationThreshold}%).
                                          {t('Please enter a justification:', 'Por favor ingresa una justificación:', lang)}
                                          <textarea
                                            className="form-control form-control-sm mt-2"
                                            rows={2}
                                            placeholder={t('Explain why the quantity differs from the recipe…', 'Explica por qué la cantidad difiere de la receta…', lang)}
                                            value={ing.deviationJustification || ''}
                                            onChange={e => {
                                              const val = e.target.value;
                                              setIngredients(prev => prev.map((x, i) =>
                                                i === idx ? { ...x, deviationJustification: val } : x
                                              ));
                                              setIngredientsSaved(false);
                                            }}
                                          />
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                )}
                                {(() => {
                                  const docLog = receivingDocsMap[`${ing.ingredientId}:${ing.ingredientLotCode}`];
                                  if (!docLog) return null;
                                  const checksPassed = (docLog.CheckResults || []).filter(c => c.passed).length;
                                  const checksTotal = (docLog.CheckResults || []).length;
                                  const docs = docLog.Documents || [];
                                  return (
                                    <tr>
                                      <td colSpan={colSpan} className="p-0">
                                        <div className="px-3 py-2 border-top d-flex flex-wrap align-items-center gap-3" style={{ fontSize: 12, background: '#f8f9fa' }}>
                                          {docLog.supplierName && (
                                            <span><span className="text-muted">{t('Supplier:', 'Proveedor:', lang)}</span> <strong>{docLog.supplierName}</strong></span>
                                          )}
                                          {docLog.deliveryDate && (
                                            <span><span className="text-muted">{t('Delivered:', 'Entregado:', lang)}</span> <strong>{new Date(docLog.deliveryDate).toLocaleDateString()}</strong></span>
                                          )}
                                          {docLog.isTemperatureControlled && docLog.receivedTemperature != null && (
                                            <span>
                                              <span className="text-muted">{t('Recv Temp:', 'Temp Recep:', lang)}</span> <strong>{docLog.receivedTemperature}°C{docLog.isFrozen ? ' ❄' : ''}</strong>
                                            </span>
                                          )}
                                          {checksTotal > 0 && (
                                            <span className={checksPassed === checksTotal ? 'text-success fw-semibold' : 'text-warning fw-semibold'}>
                                              {checksPassed === checksTotal ? '✓' : '⚠'} {checksPassed}/{checksTotal} {t('receiving checks', 'verificaciones de recepción', lang)}
                                            </span>
                                          )}
                                          {docs.map(doc => {
                                            const label = { bol: 'BOL', receipt: 'Receipt', other: 'Doc' }[doc.documentType] || 'Doc';
                                            return (
                                              <button
                                                key={doc.id}
                                                className="btn btn-outline-secondary btn-sm py-0 px-2"
                                                style={{ fontSize: 11 }}
                                                onClick={async () => {
                                                  try {
                                                    const { url } = await ingredientInventoryService.getDocumentUrlById(docLog.id, doc.id);
                                                    window.open(url, '_blank');
                                                  } catch { alert(t('Could not load document.', 'No se pudo cargar el documento.', lang)); }
                                                }}
                                              >
                                                📎 {label}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })()}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Summary badges */}
                      <div className="d-flex gap-2 mt-3 flex-wrap">
                        {['ENTERED', 'MISSING', 'EXCLUDED'].map(s => {
                          const count = ingredients.filter(i => i.status === s).length;
                          if (count === 0) return null;
                          const color = { ENTERED: 'success', MISSING: 'danger', EXCLUDED: 'secondary' };
                          return (
                            <span key={s} className={`badge bg-${color[s]}`} style={{ fontSize: 12 }}>
                              {count} {s}
                            </span>
                          );
                        })}
                      </div>

                      {/* Missing warning */}
                      {!isLocked && ingredients.some(i => i.status === 'MISSING') && (
                        <div className="alert alert-warning mt-3 mb-0" style={{ fontSize: 13 }}>
                          ⚠ <strong>{ingredients.filter(i => i.status === 'MISSING').length} {t('ingredient(s)', 'ingrediente(s)', lang)}</strong> {t('still have a missing lot code.', 'aún tienen un código de lote faltante.', lang)}
                          {t('You can still verify, but missing ingredients will be flagged in the record.', 'Aún puedes verificar, pero los ingredientes faltantes serán marcados en el registro.', lang)}
                        </div>
                      )}
                    </>
                  )}

                  {/* Add ingredient row — always visible when not locked */}
                  {!isLocked && (
                    <div className={`${ingredients.length > 0 ? 'border-top mt-3' : ''} pt-3`}>
                      <div style={{ fontWeight: 700, fontSize: 13 }} className="mb-2">{t('Add Ingredient', 'Agregar Ingrediente', lang)}</div>
                      <div className="row g-2 align-items-end">
                        <div className="col-12 col-md-3">
                          <select
                            className="form-select form-select-sm"
                            value={newRow.ingredientId}
                            onChange={e => {
                              const id = e.target.value;
                              setNewRow(r => ({ ...r, ingredientId: id, ingredientLotCode: '' }));
                              if (id && !availableLotCodes[id]) {
                                ingredientInventoryService.getLotCodesAvailable(id)
                                  .then(lcs => setAvailableLotCodes(prev => ({ ...prev, [id]: lcs })))
                                  .catch(() => {});
                              }
                            }}
                          >
                            <option value="">{t('Select ingredient…', 'Selecciona ingrediente…', lang)}</option>
                            {allIngredients.map(i => (
                              <option key={i.id} value={i.id}>{i.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12 col-md-3">
                          {(() => {
                            const newRowDatalistId = 'lc-list-newrow';
                            const newRowAvailable = availableLotCodes[newRow.ingredientId] || [];
                            const newRowLc = (newRow.ingredientLotCode || '').trim().toUpperCase();
                            const newRowNotInInventory = newRowLc && newRowAvailable.length > 0 &&
                              !newRowAvailable.some(a => a.lotCode.toUpperCase() === newRowLc);
                            return (
                              <>
                                <datalist id={newRowDatalistId}>
                                  {newRowAvailable.map(a => (
                                    <option key={a.lotCode} value={a.lotCode}>
                                      {a.lotCode} — {Number(a.remainingKg).toFixed(1)} kg remaining
                                    </option>
                                  ))}
                                </datalist>
                                <input
                                  type="text"
                                  list={newRowDatalistId}
                                  className="form-control form-control-sm"
                                  placeholder={t('Lot code', 'Código de lote', lang)}
                                  value={newRow.ingredientLotCode}
                                  onChange={e => {
                                    const val = e.target.value;
                                    setNewRow(r => ({ ...r, ingredientLotCode: val }));
                                    // If ingredient not yet fetched, fetch now
                                    if (newRow.ingredientId && !availableLotCodes[newRow.ingredientId]) {
                                      ingredientInventoryService.getLotCodesAvailable(newRow.ingredientId)
                                        .then(lcs => setAvailableLotCodes(prev => ({ ...prev, [newRow.ingredientId]: lcs })))
                                        .catch(() => {});
                                    }
                                  }}
                                />
                                {newRowNotInInventory && (
                                  <div className="text-warning mt-1" style={{ fontSize: 11 }}>
                                    ⚠ {t('Lot code not found in inventory', 'Código de lote no encontrado en inventario', lang)}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        <div className="col-6 col-md-2">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder={t('Qty', 'Cant', lang)}
                            min={0}
                            step="0.01"
                            value={newRow.quantityInput}
                            onChange={e => setNewRow(r => ({ ...r, quantityInput: e.target.value }))}
                          />
                        </div>
                        <div className="col-3 col-md-1">
                          <select
                            className="form-select form-select-sm"
                            value={newRow.uomInput}
                            onChange={e => setNewRow(r => ({ ...r, uomInput: e.target.value }))}
                          >
                            <option value="lb">lb</option>
                            <option value="kg">kg</option>
                          </select>
                        </div>
                        <div className="col-3 col-md-2">
                          <button
                            className="btn btn-primary btn-sm w-100"
                            onClick={addIngredientRow}
                            disabled={!newRow.ingredientId || savingIngredients}
                          >
                            {savingIngredients ? '…' : `+ ${t('Add', 'Agregar', lang)}`}
                          </button>
                        </div>
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        {t('You can add the same ingredient multiple times with different lot codes.', 'Puedes agregar el mismo ingrediente varias veces con diferentes códigos de lote.', lang)}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Packaging checklist answers ── */}
        {packagingAnswers.length > 0 && (
          <div className="col-12 col-lg-6">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted mb-3">{t('Packaging Checklist', 'Lista de Empaque', lang)}</h6>
                {packagingAnswers.map((a, idx) => (
                  <div key={idx} className="d-flex justify-content-between align-items-start border-bottom py-2" style={{ fontSize: 13 }}>
                    <span>{a.question}</span>
                    <span className="ms-3" style={{ minWidth: 60, textAlign: 'right', fontWeight: 700 }}>
                      {a.type === 'CHECK'
                        ? (a.value === true ? <span className="text-success">✓ {t('Yes', 'Sí', lang)}</span> : <span className="text-danger">✗ {t('No', 'No', lang)}</span>)
                        : String(a.value ?? '—')
                      }
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Editable QA fields ── */}
        <div className={`col-12 ${packagingAnswers.length > 0 ? 'col-lg-6' : ''}`}>
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">
                {t('Production Quantities', 'Cantidades de Producción', lang)}
                {!isLocked && <span className="text-muted ms-2" style={{ fontSize: 11, fontWeight: 400 }}>{t('Editable before verification', 'Editable antes de verificar', lang)}</span>}
              </h6>

              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>{t('Product', 'Producto', lang)}</th>
                    <th style={{ width: 130 }}>{t('Units Produced', 'Unidades Producidas', lang)}</th>
                    <th style={{ width: 130 }}>{t('Discarded', 'Descartadas', lang)}</th>
                  </tr>
                </thead>
                <tbody>
                  {productTotals.length === 0 ? (
                    <tr><td colSpan={3} className="text-muted">{t('No product totals recorded.', 'No hay totales de producto registrados.', lang)}</td></tr>
                  ) : (
                    <>
                      {productTotals.map((r, idx) => (
                        <tr key={r.productId}>
                          <td style={{ fontWeight: 700 }}>{r.name}</td>
                          <td>
                            {isLocked ? (
                              <span style={{ fontWeight: 900 }}>{r.units}</span>
                            ) : (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={r.units}
                                min={0}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? '' : Number(e.target.value);
                                  setProductTotals(prev =>
                                    prev.map((x, i) => i === idx ? { ...x, units: val } : x)
                                  );
                                }}
                              />
                            )}
                          </td>
                          <td>
                            {isLocked ? (
                              <span style={{ fontWeight: 900 }}>{r.discarded ?? 0}</span>
                            ) : (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={r.discarded ?? 0}
                                min={0}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? 0 : Number(e.target.value);
                                  setProductTotals(prev =>
                                    prev.map((x, i) => i === idx ? { ...x, discarded: val } : x)
                                  );
                                }}
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: '2px solid #dee2e6' }}>
                        <td style={{ fontWeight: 900 }}>TOTAL</td>
                        <td style={{ fontWeight: 900 }}>{editedTotal} {t('units', 'unidades', lang)}</td>
                        <td style={{ fontWeight: 900 }}>
                          {productTotals.reduce((s, r) => s + (Number(r.discarded) || 0), 0) || '—'}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Deviation & notes ── */}
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">{t('Deviation & Notes', 'Desviación y Notas', lang)}</h6>

              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="qaDeviation"
                  checked={deviation}
                  onChange={e => setDeviation(e.target.checked)}
                  disabled={isLocked}
                />
                <label className="form-check-label" htmlFor="qaDeviation">
                  {t('Deviation occurred', 'Se produjo una desviación', lang)}
                </label>
              </div>

              {deviation && (
                <div className="row g-2 mb-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label mb-1">{t('Deviation Notes', 'Notas de Desviación', lang)}</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={deviationNotes}
                      onChange={e => setDeviationNotes(e.target.value)}
                      disabled={isLocked}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label mb-1">{t('Corrective Action Notes', 'Notas de Acción Correctiva', lang)}</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={correctiveNotes}
                      onChange={e => setCorrectiveNotes(e.target.value)}
                      disabled={isLocked}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="form-label mb-1">{t('QA Notes (optional)', 'Notas de QA (opcional)', lang)}</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  disabled={isLocked}
                  placeholder={t('Any additional observations from QA review…', 'Cualquier observación adicional de la revisión de QA…', lang)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Verify button ── */}
        {!isLocked && (
          <div className="col-12">
            {(() => {
              const missingLotCodes = ingredients.filter(i => i.status === 'MISSING');
              const canVerify = editedTotal > 0 && missingLotCodes.length === 0;
              return (
                <div className={`card border-${canVerify ? 'success' : 'warning'}`}>
                  <div className="card-body">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                      <div>
                        <div style={{ fontWeight: 900, fontSize: 16 }}>{t('Ready to verify?', '¿Listo para verificar?', lang)}</div>
                        <div className="text-muted" style={{ fontSize: 13 }}>
                          {t('This will lock the run and create the production log entries. This action cannot be undone.', 'Esto bloqueará la corrida y creará las entradas del registro de producción. Esta acción no se puede deshacer.', lang)}
                        </div>
                        {editedTotal === 0 && (
                          <div className="text-danger mt-1" style={{ fontSize: 13 }}>
                            ⚠ {t('Total units is 0 — review product quantities before verifying.', 'El total de unidades es 0 — revisa las cantidades de producto antes de verificar.', lang)}
                          </div>
                        )}
                        {missingLotCodes.length > 0 && (
                          <div className="text-danger mt-1" style={{ fontSize: 13 }}>
                            ⚠ {t('Missing lot codes for:', 'Códigos de lote faltantes para:', lang)} {missingLotCodes.map(i => i.Ingredient?.name || `Ingredient #${i.ingredientId}`).join(', ')}.
                            {t('Save lot codes above before verifying.', 'Guarda los códigos de lote arriba antes de verificar.', lang)}
                          </div>
                        )}
                      </div>
                      <button
                        className="btn btn-success btn-lg"
                        style={{ fontWeight: 900, minWidth: 180 }}
                        onClick={() => setShowConfirm(true)}
                        disabled={submitting || !canVerify}
                      >
                        {submitting ? t('Verifying…', 'Verificando…', lang) : t('VERIFY & LOCK', 'VERIFICAR Y BLOQUEAR', lang)}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

      </div>{/* end row */}

      {/* ── Confirm modal ── */}
      {showConfirm && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.55)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('Confirm Verification', 'Confirmar Verificación', lang)}</h5>
                <button className="btn-close" onClick={() => setShowConfirm(false)} />
              </div>
              <div className="modal-body">
                <p>{t('You are about to verify and lock batch', 'Estás a punto de verificar y bloquear el lote')} <strong>{run.Batch?.lotCode}</strong>.</p>
                <p>{t('This will:', 'Esto hará:', lang)}</p>
                <ul>
                  <li>{t('Lock the run permanently (no further edits)', 'Bloquear la corrida permanentemente (sin más ediciones)', lang)}</li>
                  <li>{t('Create production log entries for', 'Crear entradas de registro de producción por', lang)} <strong>{editedTotal} {t('total units', 'unidades totales', lang)}</strong></li>
                </ul>
                {deviation && (
                  <div className="alert alert-warning" style={{ fontSize: 13 }}>
                    ⚠ {t('This batch has a', 'Este lote tiene una', lang)} <strong>{t('deviation', 'desviación', lang)}</strong> {t('recorded. Make sure notes are complete.', 'registrada. Asegúrate de que las notas estén completas.', lang)}
                  </div>
                )}
                {ingredients.filter(i => i.status === 'MISSING').length > 0 && (
                  <div className="alert alert-danger" style={{ fontSize: 13 }}>
                    ⚠ <strong>{t('Missing lot codes:', 'Códigos de lote faltantes:', lang)}</strong>{' '}
                    {ingredients.filter(i => i.status === 'MISSING').map(i => i.Ingredient?.name || `Ingredient #${i.ingredientId}`).join(', ')}.
                  </div>
                )}
                <p className="mb-0 text-muted" style={{ fontSize: 13 }}>{t('This action cannot be undone.', 'Esta acción no se puede deshacer.', lang)}</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowConfirm(false)} disabled={submitting}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button className="btn btn-success" onClick={doVerify} disabled={submitting}>
                  {submitting ? t('Verifying…', 'Verificando…', lang) : t('Confirm & Verify', 'Confirmar y Verificar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
    </>
  );
}